import { Link } from "react-router-dom";
import { Phone, Mail, Globe, CreditCard, Shield, TrendingUp, ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";

const LOGO_URL = "/logo.png";

export default function LandingPage() {
  const features = [
    {
      icon: <CreditCard className="w-8 h-8" />,
      title: "Credit Card Solutions",
      description: "Expert assistance for those struggling with credit card payments"
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Secure & Confidential",
      description: "Your financial information is protected with bank-level security"
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: "Personalized Plans",
      description: "Custom solutions tailored to your unique financial situation"
    }
  ];

  const steps = [
    "Create your account and verify your email",
    "Add your credit cards and creditor information",
    "Upload your credit report for analysis",
    "Receive your personalized debt solution plan"
  ];

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/60 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img 
              src={LOGO_URL} 
              alt="Credit2Cash" 
              className="h-16 logo-glow"
              data-testid="nav-logo"
            />
          </Link>
          <div className="flex items-center gap-4">
            <a href="tel:18888714928" className="hidden md:flex items-center gap-2 text-[#FFD700] font-semibold">
              <Phone className="w-4 h-4" />
              1 (888) 871-4928
            </a>
            <Link to="/login">
              <Button 
                variant="outline" 
                className="border-white/20 text-white hover:bg-white/10"
                data-testid="nav-login-btn"
              >
                Login
              </Button>
            </Link>
            <Link to="/register">
              <Button 
                className="bg-[#00FF66] text-black font-bold hover:bg-[#00FF66]/90 neon-glow-green"
                data-testid="nav-register-btn"
              >
                Get Started
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-section min-h-screen flex items-center pt-24">
        <div className="max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-8">
            <div className="logo-container">
              <img 
                src={LOGO_URL} 
                alt="Credit2Cash" 
                className="w-full max-w-lg logo-glow"
                data-testid="hero-logo"
              />
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter">
              <span className="text-white">Turn Your</span>
              <br />
              <span className="text-gradient-gold">Credit Into Cash</span>
            </h1>
            <p className="text-lg text-[#A1A1AA] max-w-lg">
              Struggling with credit card debt? We specialize in helping people close to default 
              find real solutions. Get your personalized plan today.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/register">
                <Button 
                  size="lg"
                  className="bg-[#00FF66] text-black font-bold text-lg px-8 py-6 hover:bg-[#00FF66]/90 cta-pulse"
                  data-testid="hero-cta-btn"
                >
                  Start Your Plan
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href="tel:18888714928">
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-[#FFD700]/50 text-[#FFD700] font-bold text-lg px-8 py-6 hover:bg-[#FFD700]/10"
                  data-testid="hero-call-btn"
                >
                  <Phone className="mr-2 w-5 h-5" />
                  Call Us Now
                </Button>
              </a>
            </div>
          </div>
          
          <div className="hidden lg:block">
            <div className="premium-card rounded-3xl p-8 space-y-6">
              <h3 className="text-2xl font-bold text-white">Why Choose Credit2Cash?</h3>
              <ul className="space-y-4">
                {["Expert financial guidance", "Fast approval process", "Flexible payment solutions", "24/7 customer support"].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-[#A1A1AA]">
                    <CheckCircle2 className="w-5 h-5 text-[#00FF66]" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="pt-4 border-t border-white/10">
                <p className="text-sm text-[#71717A] font-mono">TRUSTED BY THOUSANDS</p>
                <p className="text-3xl font-black text-gradient-green mt-2">$10M+ Resolved</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4">
            <span className="text-white">How We </span>
            <span className="text-[#00FF66]">Help You</span>
          </h2>
          <p className="text-center text-[#A1A1AA] mb-16 max-w-2xl mx-auto">
            Our streamlined process makes it easy to get the help you need
          </p>
          
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, i) => (
              <div 
                key={i}
                className="premium-card rounded-2xl p-8 card-hover"
                data-testid={`feature-card-${i}`}
              >
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-[#00FF66]/20 to-[#FFD700]/20 flex items-center justify-center text-[#00FF66] mb-6">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
                <p className="text-[#A1A1AA]">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 px-6 bg-[#111]">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-16">
            <span className="text-white">Get Started in </span>
            <span className="text-[#FFD700]">4 Simple Steps</span>
          </h2>
          
          <div className="grid md:grid-cols-4 gap-6">
            {steps.map((step, i) => (
              <div key={i} className="relative">
                <div className="premium-card rounded-2xl p-6 h-full" data-testid={`step-card-${i}`}>
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#00FF66] to-[#00F0FF] flex items-center justify-center text-black font-black text-xl mb-4">
                    {i + 1}
                  </div>
                  <p className="text-[#A1A1AA]">{step}</p>
                </div>
                {i < steps.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2">
                    <ArrowRight className="w-6 h-6 text-[#00FF66]" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="premium-card rounded-3xl p-12 neon-border">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Ready to Take Control of Your Finances?
            </h2>
            <p className="text-[#A1A1AA] mb-8 max-w-2xl mx-auto">
              Don't let credit card debt hold you back. Join thousands who have transformed 
              their financial future with Credit2Cash.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/register">
                <Button 
                  size="lg"
                  className="bg-[#00FF66] text-black font-bold text-lg px-10 py-6 hover:bg-[#00FF66]/90 cta-pulse"
                  data-testid="cta-register-btn"
                >
                  Create Free Account
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div className="md:col-span-2">
              <img 
                src={LOGO_URL} 
                alt="Credit2Cash" 
                className="h-20 mb-4 logo-glow"
                data-testid="footer-logo"
              />
              <p className="text-[#A1A1AA] max-w-sm">
                Helping individuals navigate credit challenges and find lasting financial solutions.
              </p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Contact Us</h4>
              <ul className="space-y-3">
                <li>
                  <a href="tel:18888714928" className="flex items-center gap-2 text-[#A1A1AA] hover:text-[#FFD700] transition-colors">
                    <Phone className="w-4 h-4" />
                    1 (888) 871-4928
                  </a>
                </li>
                <li>
                  <a href="mailto:cash@credit2cash.store" className="flex items-center gap-2 text-[#A1A1AA] hover:text-[#00FF66] transition-colors">
                    <Mail className="w-4 h-4" />
                    cash@credit2cash.store
                  </a>
                </li>
                <li>
                  <a href="https://credit2cash.store" className="flex items-center gap-2 text-[#A1A1AA] hover:text-[#00F0FF] transition-colors">
                    <Globe className="w-4 h-4" />
                    credit2cash.store
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/login" className="text-[#A1A1AA] hover:text-white transition-colors">
                    Login
                  </Link>
                </li>
                <li>
                  <Link to="/register" className="text-[#A1A1AA] hover:text-white transition-colors">
                    Register
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-white/5 text-center">
            <p className="text-[#71717A] text-sm">
              © {new Date().getFullYear()} Credit2Cash. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
